import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, API } from "@/lib/api";
import TierOneRail from "@/components/TierOneRail";

// Older ReportLab-generated docs — kept live but rendered below the v2 report cards.
const DOCS = [
  {
    key: "full_depth",
    label: "The Full-Depth Brief",
    length: "15–25 pages",
    who: "Full evaluation record · internal + trusted advisors",
    body: "Executive summary, problem, solution, market, revenue model, GTM, roadmap, risks, transcripts.",
    accent: "border-emerald-200",
    eyebrow: "ring-accent-1",
  },
  {
    key: "one_pager",
    label: "The Investor One-Pager",
    length: "1 page · 90-sec read",
    who: "External · investors",
    body: "Problem, solution, why now, market, model, traction, ask, contact.",
    accent: "border-violet-200",
    eyebrow: "ring-accent-3",
  },
  {
    key: "panel_report",
    label: "The Shark Panel Report",
    length: "3–5 pages · 10 personas",
    who: "Fundraising strategy · you + your advisors",
    body: "Angel + seed panel results, per-persona pass/fail with evidence, biggest gaps to close, readiness label.",
    accent: "border-blue-300",
    eyebrow: "ring-accent-4",
  },
];

export default function Documents() {
  const { id } = useParams();
  const nav = useNavigate();
  const [idea, setIdea] = useState(null);
  const [availability, setAvailability] = useState(null);

  useEffect(() => {
    (async () => {
      const [ideaResp, availResp] = await Promise.all([
        api.get(`/ideas/${id}`),
        api.get(`/ideas/${id}/documents-available`).catch(() => ({ data: null })),
      ]);
      setIdea(ideaResp.data.idea);
      setAvailability(availResp.data);
    })();
  }, [id]);

  const availableSet = new Set(availability?.available || []);
  const stage = availability?.stage || "locked";

  async function download(docKey) {
    try {
      const url = `${API}/ideas/${id}/docs/${docKey}`;
      const resp = await fetch(url, { credentials: "include" });
      if (!resp.ok) throw new Error(await resp.text());
      const blob = await resp.blob();
      const dlUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = dlUrl;
      a.download = `${docKey}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(dlUrl);
    } catch (e) {
      alert("Download failed: " + e.message);
    }
  }

  if (!idea) return <div className="p-14 text-slate-500">Loading…</div>;

  return (
    <div className="min-h-screen">
      <div className="max-w-[1120px] mx-auto px-8 py-14">
        <button
          className="mono-caps hover:text-slate-900 transition-colors mb-6"
          onClick={() => nav(`/ideas/${id}/verdict`)}
          data-testid="docs-back-to-verdict"
        >
          ← Verdict
        </button>
        <TierOneRail idea={idea} ideaId={id} />
        <div className="mono-caps mb-3">Document package</div>
        <h1 className="font-display text-4xl sm:text-5xl mb-4">{idea.title}</h1>

        {stage === "tier1" ? (
          <p className="text-slate-700 max-w-2xl mb-8" data-testid="docs-ring3-header">
            Tier 1 complete. The three reports below plus the handoff markdown and slide deck
            are the go-to-market package. Investor-facing docs unlock once Tier 2 (Ring 4 +
            Shark Panel) runs.
          </p>
        ) : stage === "tier2" ? (
          <p className="text-slate-700 max-w-2xl mb-8" data-testid="docs-complete-header">
            Tier 2 complete. Every document below is downloadable — go-to-market plus investor-facing.
          </p>
        ) : (
          <p className="text-slate-600 max-w-2xl mb-8">
            Documents unlock as rings clear. Continue the interrogation to advance.
          </p>
        )}

        {/* RING-TIER-03 · Feature 10 — Two-package downloads */}
        <div className="mb-8 grid gap-4 md:grid-cols-2" data-testid="tier-packages">
          <div
            className="rounded-xl border-2 border-emerald-500 bg-emerald-50 p-5"
            data-testid="tier1-package-card"
          >
            <div className="mono-caps text-[0.65rem] text-emerald-700">Tier 1 · go-to-market</div>
            <h3 className="font-semibold text-lg mt-1">Tier 1 package (zip)</h3>
            <p className="text-sm text-slate-700 mt-1">
              Executive summary, checklist, ring 3 report, deck, handoff markdown.
            </p>
            <div className="mt-3 flex gap-2">
              <a
                className={`btn-primary ${stage === "locked" ? "opacity-40 pointer-events-none" : ""}`}
                href={`${API}/ideas/${id}/package/tier1.zip`}
                target="_blank"
                rel="noreferrer"
                data-testid="download-tier1-zip"
              >
                Download Tier 1
              </a>
              <button
                type="button"
                onClick={async () => {
                  try {
                    const r = await api.post(`/ideas/${id}/regenerate-brief`);
                    toast.success(`Regenerated · v${r.data.version.version}`);
                  } catch (e) {
                    toast.error(e?.response?.data?.detail || "Regenerate failed.");
                  }
                }}
                disabled={stage === "locked"}
                data-testid="regenerate-brief-btn"
                className="mono-caps text-[0.65rem] px-3 py-2 rounded border border-emerald-600 text-emerald-800 hover:bg-emerald-100 disabled:opacity-40"
              >
                Regenerate brief
              </button>
            </div>
          </div>
          <div
            className={`rounded-xl border-2 p-5 ${
              stage === "tier2" ? "border-violet-500 bg-violet-50" : "border-slate-200 bg-slate-50"
            }`}
            data-testid="tier2-package-card"
          >
            <div
              className={`mono-caps text-[0.65rem] ${
                stage === "tier2" ? "text-violet-700" : "text-slate-500"
              }`}
            >
              Tier 2 · investor-ready
            </div>
            <h3 className="font-semibold text-lg mt-1">Tier 2 package (zip)</h3>
            <p className="text-sm text-slate-600 mt-1">
              Adds investor one-pager and Shark Panel report. Unlocks after Ring 4 + Panel run.
            </p>
            <div className="mt-3">
              <a
                className={`btn-primary ${stage !== "tier2" ? "opacity-40 pointer-events-none" : ""}`}
                href={`${API}/ideas/${id}/package/tier2.zip`}
                target="_blank"
                rel="noreferrer"
                data-testid="download-tier2-zip"
              >
                {stage === "tier2" ? "Download Tier 2" : "Locked"}
              </a>
            </div>
          </div>
        </div>

        {/* Primary: v2 cross-linked reports — the three Ring 3 deliverables. */}
        <div data-testid="reports-v2-section">
          <div className="grid gap-6 md:grid-cols-3 mb-6" data-testid="reports-v2-grid">
            <div className="card-flat border-[1.5px] border-slate-200 flex flex-col">
              <div className="mono-caps text-slate-500 text-[0.65rem] mb-2">Executive Summary</div>
              <h3 className="font-display text-xl mb-3">One-page verdict + linkbacks</h3>
              <p className="text-slate-600 text-sm mb-6 flex-1">
                Signal, verdict, biggest strength/risk, first move. The starting
                point for every reader.
              </p>
              <div className="flex flex-wrap gap-3">
                <a
                  className="btn-primary"
                  href={`${API}/ideas/${id}/reports/executive-summary`}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="reports-v2-exec-html"
                >
                  View in browser
                </a>
                <a
                  className="px-4 py-2 mono-caps border border-slate-300 rounded hover:bg-slate-50 transition-colors"
                  href={`${API}/ideas/${id}/reports/executive-summary.pdf`}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="reports-v2-exec-pdf"
                >
                  Download PDF
                </a>
              </div>
            </div>

            <div className="card-flat border-[1.5px] border-slate-200 flex flex-col">
              <div className="mono-caps text-slate-500 text-[0.65rem] mb-2">Checklist Scorecard</div>
              <h3 className="font-display text-xl mb-3">All 36 criteria · four states</h3>
              <p className="text-slate-600 text-sm mb-6 flex-1">
                Every criterion accounted for — pass, watch, fail, or not-yet-evaluated.
                Never silently drops a row.
              </p>
              <div className="flex flex-wrap gap-3">
                <a
                  className="btn-primary"
                  href={`${API}/ideas/${id}/reports/checklist-scorecard`}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="reports-v2-checklist-html"
                >
                  View in browser
                </a>
                <a
                  className="px-4 py-2 mono-caps border border-slate-300 rounded hover:bg-slate-50 transition-colors"
                  href={`${API}/ideas/${id}/reports/checklist-scorecard.pdf`}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="reports-v2-checklist-pdf"
                >
                  Download PDF
                </a>
              </div>
            </div>

            <div className="card-flat border-[1.5px] border-slate-200 flex flex-col">
              <div className="mono-caps text-slate-500 text-[0.65rem] mb-2">Ring 3 Report</div>
              <h3 className="font-display text-xl mb-3">Deep-trench narrative + unit economics</h3>
              <p className="text-slate-600 text-sm mb-6 flex-1">
                Ring 3 verdict, unit-economics ledger, any figures flagged as
                unsupported, and the 12 deep-trench criteria in full.
              </p>
              <div className="flex flex-wrap gap-3">
                <a
                  className="btn-primary"
                  href={`${API}/ideas/${id}/reports/ring-report`}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="reports-v2-ring-html"
                >
                  View in browser
                </a>
                <a
                  className="px-4 py-2 mono-caps border border-slate-300 rounded hover:bg-slate-50 transition-colors"
                  href={`${API}/ideas/${id}/reports/ring-report.pdf`}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="reports-v2-ring-pdf"
                >
                  Download PDF
                </a>
              </div>
            </div>
          </div>

          {/* v16 · Ring 3 exports + zip. Renders as soon as Tier 1 unlocks
              (renamed from "ring3" during RING-TIER-03). Tier 2 keeps them
              too. */}
          {(stage === "tier1" || stage === "tier2") && (
            <div
              className="flex flex-wrap items-center gap-3 mb-14 pb-8 border-b border-slate-200"
              data-testid="exports-v16-row"
            >
              <a
                className="px-4 py-2 mono-caps border border-slate-300 rounded hover:bg-slate-50 transition-colors text-sm"
                href={`${API}/idea/${id}/export.md`}
                target="_blank"
                rel="noreferrer"
                data-testid="export-md-button"
              >
                Markdown handoff (.md)
              </a>
              <a
                className="px-4 py-2 mono-caps border border-slate-300 rounded hover:bg-slate-50 transition-colors text-sm"
                href={`${API}/idea/${id}/deck.pdf`}
                target="_blank"
                rel="noreferrer"
                data-testid="export-deck-button"
              >
                Slide deck (16:9 PDF)
              </a>
              <a
                className="btn-primary text-sm"
                href={`${API}/idea/${id}/download-all`}
                target="_blank"
                rel="noreferrer"
                data-testid="export-download-all-button"
              >
                Download all documents
              </a>
            </div>
          )}
        </div>

        {/* Legacy: older ReportLab documents. Kept live but visually secondary. */}
        <div className="border-t border-slate-200 pt-10">
          <div className="mono-caps text-slate-500 mb-2">Additional documents</div>
          <h2 className="font-display text-2xl mb-3">Full package · legacy PDFs</h2>
          <p className="text-slate-600 text-sm max-w-2xl mb-8">
            The Full-Depth Brief and Shark Panel deliverables. Kept alongside the
            three primary reports above.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {DOCS.map((d) => {
              const isAvailable = availableSet.has(d.key);
              return (
                <div
                  key={d.key}
                  className={`card-flat flex flex-col border-[1.5px] ${d.accent} ${
                    isAvailable ? "" : "opacity-60"
                  }`}
                  data-testid={`doc-card-${d.key}`}
                >
                  <div className={`mono-caps mb-4 ${d.eyebrow} flex items-center justify-between`}>
                    <span>{d.length}</span>
                    {!isAvailable && (
                      <span className="text-slate-400 text-[0.6rem]" data-testid={`doc-locked-${d.key}`}>
                        Locked · needs Shark Panel
                      </span>
                    )}
                  </div>
                  <h3 className="font-display text-2xl mb-3">{d.label}</h3>
                  <div className="mono-caps text-slate-400 text-[0.65rem] mb-4">{d.who}</div>
                  <p className="text-slate-600 text-sm leading-relaxed mb-8 flex-1">{d.body}</p>
                  <button
                    className={`self-start ${isAvailable ? "btn-primary" : "px-4 py-2 mono-caps text-slate-400 border border-slate-200 rounded cursor-not-allowed"}`}
                    onClick={() => isAvailable && download(d.key)}
                    disabled={!isAvailable}
                    data-testid={`doc-download-${d.key}`}
                  >
                    {isAvailable ? "Download PDF" : "Locked"}
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
